import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Leaf, LogOut, MessageCircle, RefreshCw, Search } from "lucide-react";
import { API, GOAL_LABELS, STATUS_LABELS } from "@/config";

const TOKEN_KEY = "fitnurish_admin_token";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const errorText = (error, fallback) => { const detail = error?.response?.data?.detail; return typeof detail === "string" ? detail : fallback; };
const whatsappLink = (number) => `https://wa.me/${number.replace(/\D/g, "")}`;
const formatDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function AdminLogin({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try { const { data } = await axios.post(`${API}/admin/login`, { password }); localStorage.setItem(TOKEN_KEY, data.token); onSuccess(); }
    catch (err) { setError(errorText(err, "Could not log in. Please try again.")); }
    finally { setBusy(false); }
  };
  return <div className="admin-login-wrap">
    <form className="admin-login consult-form" onSubmit={submit} data-testid="admin-login-form">
      <p className="eyebrow"><span className="eyebrow-line" /> OWNER ACCESS</p>
      <h2>Your <i>leads.</i></h2>
      <label>Admin password<input type="password" required value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter password" data-testid="admin-password-input" /></label>
      <button className="button button-dark form-submit" type="submit" disabled={busy} data-testid="admin-login-button">{busy ? "Checking…" : "Open dashboard"}<ArrowRight size={17} /></button>
      {error && <p className="form-status error" role="alert" data-testid="admin-login-error">{error}</p>}
      <Link className="owner-link muted" to="/" data-testid="admin-back-home-link">← Back to fitnurish.com</Link>
    </form>
  </div>;
}

function NoteCell({ lead, onSave }) {
  const [value, setValue] = useState(lead.note || "");
  useEffect(() => { setValue(lead.note || ""); }, [lead.note]);
  const save = () => { if (value !== (lead.note || "")) onSave(lead.lead_id, { note: value }); };
  return <textarea className="note-input" rows={1} maxLength={500} value={value} onChange={(event) => setValue(event.target.value)} onBlur={save} placeholder="Add a private note…" data-testid="lead-note-input" />;
}

function LeadRow({ lead, onUpdate }) {
  return <tr data-testid={`lead-row-${lead.lead_id}`}>
    <td><strong data-testid="lead-name">{lead.name}</strong><small>{formatDate(lead.created_at)}</small></td>
    <td data-testid="lead-address">{lead.address}</td>
    <td><a className="wa-link" href={whatsappLink(lead.whatsapp)} target="_blank" rel="noreferrer" data-testid="lead-whatsapp-link"><MessageCircle size={14} /> {lead.whatsapp}</a></td>
    <td><span className={`goal-chip ${lead.goal}`} data-testid="lead-goal">{GOAL_LABELS[lead.goal]}</span></td>
    <td><select className={`status-select ${lead.status}`} value={lead.status} onChange={(event) => onUpdate(lead.lead_id, { status: event.target.value })} data-testid="lead-status-select">{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></td>
    <td><NoteCell lead={lead} onSave={onUpdate} /></td>
  </tr>;
}

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
function downloadCsv(leads) {
  const header = ["Name", "Address", "WhatsApp", "Goal", "Status", "Note", "Submitted"];
  const rows = leads.map((lead) => [lead.name, lead.address, lead.whatsapp, GOAL_LABELS[lead.goal], STATUS_LABELS[lead.status], lead.note || "", lead.created_at]);
  const csv = [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  const link = Object.assign(document.createElement("a"), { href: url, download: `fitnurish-leads-${new Date().toISOString().slice(0, 10)}.csv` });
  document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
}

function LeadsDashboard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [goal, setGoal] = useState("all");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await axios.get(`${API}/admin/leads`, { headers: authHeaders() }); setLeads(data.leads); }
    catch (err) { if (err?.response?.status === 401) onLogout(); else setError(errorText(err, "Could not load leads.")); }
    finally { setLoading(false); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const updateLead = async (leadId, changes) => {
    setLeads((current) => current.map((lead) => (lead.lead_id === leadId ? { ...lead, ...changes } : lead)));
    try { await axios.patch(`${API}/admin/leads/${leadId}`, changes, { headers: authHeaders() }); }
    catch (err) { setError(errorText(err, "Could not save changes.")); load(); }
  };

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return leads.filter((lead) => (goal === "all" || lead.goal === goal) && (status === "all" || lead.status === status) && (!term || [lead.name, lead.address, lead.whatsapp].some((field) => field.toLowerCase().includes(term))));
  }, [leads, query, goal, status]);
  const count = (key) => leads.filter((lead) => lead.status === key).length;

  return <div className="admin-shell" data-testid="admin-dashboard">
    <header className="admin-head">
      <div><p className="eyebrow"><span className="eyebrow-line" /> LEADS DASHBOARD</p><h2>Consultation <i>requests.</i></h2></div>
      <div className="admin-actions"><button className="ghost-button" onClick={() => downloadCsv(visible)} disabled={!visible.length} data-testid="admin-export-csv-button"><Download size={14} /> Export CSV{visible.length !== leads.length ? ` (${visible.length})` : ""}</button><button className="ghost-button" onClick={load} data-testid="admin-refresh-button"><RefreshCw size={14} /> Refresh</button><button className="ghost-button" onClick={onLogout} data-testid="admin-logout-button"><LogOut size={14} /> Log out</button></div>
    </header>
    <div className="stat-row">
      <div data-testid="stat-total"><strong>{leads.length}</strong><span>total leads</span></div>
      <div data-testid="stat-new"><strong>{count("new")}</strong><span>new</span></div>
      <div data-testid="stat-contacted"><strong>{count("contacted")}</strong><span>contacted</span></div>
      <div data-testid="stat-converted"><strong>{count("converted")}</strong><span>converted</span></div>
    </div>
    <div className="filter-bar">
      <label className="search-box"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, area or number" data-testid="admin-search-input" /></label>
      <select value={goal} onChange={(event) => setGoal(event.target.value)} data-testid="admin-goal-filter"><option value="all">All goals</option>{Object.entries(GOAL_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
      <select value={status} onChange={(event) => setStatus(event.target.value)} data-testid="admin-status-filter"><option value="all">All statuses</option>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
    </div>
    {error && <p className="form-status error" role="alert" data-testid="admin-error">{error}</p>}
    <div className="table-wrap">
      <table className="leads-table" data-testid="leads-table">
        <thead><tr><th>Name</th><th>Where</th><th>WhatsApp</th><th>Goal</th><th>Status</th><th>Notes</th></tr></thead>
        <tbody>{visible.map((lead) => <LeadRow key={lead.lead_id} lead={lead} onUpdate={updateLead} />)}</tbody>
      </table>
      {loading && <p className="table-empty" data-testid="admin-loading">Loading leads…</p>}
      {!loading && visible.length === 0 && <p className="table-empty" data-testid="admin-empty">{leads.length ? "No leads match these filters." : "No consultation requests yet. They’ll appear here the moment someone fills in the form."}</p>}
    </div>
  </div>;
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);
  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }, []);
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setAuthed(false); return; }
    axios.get(`${API}/admin/me`, { headers: authHeaders() }).then(() => setAuthed(true)).catch(logout);
  }, [logout]);
  return <div className="admin-page">
    <header className="navbar"><Link className="brand" to="/" data-testid="admin-brand-link"><span className="brand-mark"><Leaf size={17} /></span><span>fitnurish<em>.com</em></span></Link><span className="admin-tag">Owner area</span></header>
    {authed === null ? <p className="table-empty">Checking session…</p> : authed ? <LeadsDashboard onLogout={logout} /> : <AdminLogin onSuccess={() => setAuthed(true)} />}
  </div>;
}
